import { createContext, useContext, useReducer } from 'react';
import type {
  Dispatch,
  JSX,
  PropsWithChildren,
  ReactNode,
  Reducer,
} from 'react';

import { animated, useTransition } from '@react-spring/web';
import { produce } from 'immer';
import ReactDOM from 'react-dom';

import Toast, { ToastType } from '../../atoms/Toast/Toast';
import usePortal from '../../hooks/usePortal';

import styles from './ToastProvider.module.css';

export interface Notification {
  id: string;
  type: keyof typeof ToastType;
  message: ReactNode;
}

type Action =
  | {
      type: 'PULL';
      payload: string;
    }
  | {
      type: 'PUSH';
      payload: Omit<Notification, 'id'>;
    };

export interface State {
  notifications: Notification[];
}

const DEFAULT_STATE: State = {
  notifications: [],
};

const ToastStateContext = createContext<State>(DEFAULT_STATE);
ToastStateContext.displayName = 'ToastStateContext';
export const ToastDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined,
);
ToastDispatchContext.displayName = 'ToastDispatchContext';

const MAX_VISIBLE_TOASTS = 4;

const reducer = produce((draft: State, action: Action) => {
  switch (action.type) {
    case 'PUSH': {
      draft.notifications.push({
        id: window.crypto.randomUUID(),
        ...action.payload,
      });
      if (draft.notifications.length > MAX_VISIBLE_TOASTS) {
        draft.notifications.splice(
          0,
          draft.notifications.length - MAX_VISIBLE_TOASTS,
        );
      }
      break;
    }
    case 'PULL': {
      draft.notifications = draft.notifications.filter(
        (process) => process.id !== action.payload,
      );
      break;
    }
    default:
      throw new Error();
  }
});

export function ToastProvider({ children }: PropsWithChildren): JSX.Element {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(
    reducer,
    DEFAULT_STATE,
  );
  const target = usePortal('root');
  const transition = useTransition(state.notifications, {
    keys: (notification) => notification.id,
    from: { transform: 'translateX(100%)', opacity: 0 },
    enter: { transform: 'translateX(0)', opacity: 1 },
    leave: { transform: 'translateX(100%)', opacity: 0 },
  });

  return (
    <ToastDispatchContext.Provider value={dispatch}>
      {children}

      {state.notifications.length > 0 &&
        ReactDOM.createPortal(
          <div className={styles.Toasts}>
            {transition((style, notification) => (
              <animated.div style={style} key={notification.id}>
                <Toast
                  type={notification.type}
                  message={notification.message}
                  onDismiss={() =>
                    dispatch({ type: 'PULL', payload: notification.id })
                  }
                />
              </animated.div>
            ))}
          </div>,
          target,
        )}
    </ToastDispatchContext.Provider>
  );
}

export function useToastDispatchContext(): Dispatch<Action> | undefined {
  return useContext(ToastDispatchContext);
}
